import React, { useCallback, useEffect, useRef, useState } from "react";

const EmbroideryCanvas = ({ onDone }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return undefined;

    let raf = 0;
    let cancelled = false;
    let finished = false;

    const W = 680;
    const H = 360;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = W * dpr;
    cv.height = H * dpr;
    cv.style.width = "100%";
    cv.style.height = "auto";

    const ctx = cv.getContext("2d");
    ctx.scale(dpr, dpr);

    function drawFabric() {
      ctx.fillStyle = "rgba(255, 252, 247, 0.82)";
      ctx.fillRect(0, 0, W, H);

      for (let x = 0; x < W; x += 4) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.strokeStyle = "rgba(72, 50, 34, 0.026)";
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      for (let y = 0; y < H; y += 4) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.strokeStyle = "rgba(72, 50, 34, 0.026)";
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      const vignette = ctx.createRadialGradient(W / 2, H / 2, 10, W / 2, H / 2, W * 0.72);
      vignette.addColorStop(0, "rgba(255,255,255,0.08)");
      vignette.addColorStop(1, "rgba(132, 91, 55, 0.08)");
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, W, H);
    }

    function rasterFill(ch, font, startX, baselineY, step) {
      const oc = document.createElement("canvas");
      oc.width = W;
      oc.height = H;
      const ox = oc.getContext("2d");
      ox.font = font;
      ox.textBaseline = "alphabetic";
      ox.fillStyle = "#000";
      ox.fillText(ch, startX, baselineY);
      const d = ox.getImageData(0, 0, W, H).data;
      const cols = {};
      for (let y = 0; y < H; y += step) {
        for (let x = 0; x < W; x += step) {
          if (d[(y * W + x) * 4 + 3] > 40) {
            if (!cols[x]) cols[x] = [];
            cols[x].push(y);
          }
        }
      }
      const keys = Object.keys(cols).map(Number).sort((a, b) => a - b);
      const pts = [];
      keys.forEach((k, i) => {
        const col = cols[k].sort((a, b) => a - b);
        if (i % 2 === 1) col.reverse();
        col.forEach((y) => pts.push({ x: k, y }));
      });
      return pts;
    }

    function splitSegments(pts, maxGap) {
      const segments = [];
      if (!pts.length) return segments;
      let cur = [pts[0]];
      for (let i = 1; i < pts.length; i += 1) {
        const dx = pts[i].x - pts[i - 1].x;
        const dy = pts[i].y - pts[i - 1].y;
        const dist = Math.hypot(dx, dy);
        if (dist > maxGap) {
          segments.push(cur);
          cur = [pts[i]];
        } else {
          cur.push(pts[i]);
        }
      }
      segments.push(cur);
      return segments;
    }

    function traceContour(ch, font, startX, baselineY) {
      const oc = document.createElement("canvas");
      oc.width = W;
      oc.height = H;
      const ox = oc.getContext("2d");
      ox.font = font;
      ox.textBaseline = "alphabetic";
      ox.fillStyle = "#000";
      ox.fillText(ch, startX, baselineY);
      const d = ox.getImageData(0, 0, W, H).data;
      const filled = (x, y) => {
        if (x < 0 || y < 0 || x >= W || y >= H) return false;
        return d[(y * W + x) * 4 + 3] > 40;
      };
      const border = new Set();
      const bPts = [];
      for (let y = 0; y < H; y += 1) {
        for (let x = 0; x < W; x += 1) {
          if (filled(x, y) && (!filled(x - 1, y) || !filled(x + 1, y) || !filled(x, y - 1) || !filled(x, y + 1))) {
            const k = y * W + x;
            if (!border.has(k)) {
              border.add(k);
              bPts.push({ x, y });
            }
          }
        }
      }
      if (!bPts.length) return [];
      bPts.sort((a, b) => a.x - b.x || a.y - b.y);
      const used = new Uint8Array(W * H);
      const map = {};
      bPts.forEach((p) => {
        map[p.y * W + p.x] = p;
      });
      const result = [];
      let cur = bPts[0];
      used[cur.y * W + cur.x] = 1;
      result.push(cur);
      for (let iter = 0; iter < bPts.length - 1; iter += 1) {
        let best = null;
        let bestD = Infinity;
        const R = 4;
        for (let dy = -R; dy <= R; dy += 1) {
          for (let dx = -R; dx <= R; dx += 1) {
            const nx = cur.x + dx;
            const ny = cur.y + dy;
            const k = ny * W + nx;
            if (map[k] && !used[k]) {
              const d2 = dx * dx + dy * dy;
              if (d2 < bestD) {
                bestD = d2;
                best = map[k];
              }
            }
          }
        }
        if (!best) break;
        used[best.y * W + best.x] = 1;
        result.push(best);
        cur = best;
      }
      return result;
    }

    function thread(x1, y1, x2, y2, i, thin) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const len = Math.hypot(dx, dy);
      if (len < 0.1 || len > 10) return;
      const nx = dy / len;
      const ny = -dx / len;
      const tw = Math.sin(i * 2.6) * 0.65;
      const lw = thin ? 1.35 : 2.05;

      ctx.beginPath();
      ctx.moveTo(x1 + nx * tw, y1 + ny * tw);
      ctx.lineTo(x2 + nx * tw, y2 + ny * tw);
      ctx.strokeStyle = "rgba(26, 18, 12, 0.94)";
      ctx.lineWidth = lw;
      ctx.lineCap = "round";
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x1 + nx * (tw + 0.9), y1 + ny * (tw + 0.9));
      ctx.lineTo(x2 + nx * (tw + 0.9), y2 + ny * (tw + 0.9));
      ctx.strokeStyle = "rgba(255, 241, 210, 0.34)";
      ctx.lineWidth = 0.55;
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x1 + nx * (tw - 0.9), y1 + ny * (tw - 0.9));
      ctx.lineTo(x2 + nx * (tw - 0.9), y2 + ny * (tw - 0.9));
      ctx.strokeStyle = "rgba(82, 56, 38, 0.30)";
      ctx.lineWidth = 0.55;
      ctx.stroke();

      if (i % 5 === 0) {
        ctx.beginPath();
        ctx.arc((x1 + x2) / 2 + nx * tw, (y1 + y2) / 2 + ny * tw, 0.42, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(245, 229, 190, 0.38)";
        ctx.fill();
      }
    }

    function drawGroup(pts, n, thin) {
      const end = n === undefined ? pts.length : Math.min(n, pts.length);
      for (let i = 0; i < end - 1; i += 1) thread(pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y, i, !!thin);
    }

    function drawNeedle(tipX, tipY) {
      const L = 58;
      const eyeX = -14;
      const x = tipX - L + 4;
      const y = tipY;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(0.04);
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.35)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 3;
      ctx.shadowOffsetY = 4;
      const g = ctx.createLinearGradient(0, -3, 0, 3);
      g.addColorStop(0, "#e8e8e8");
      g.addColorStop(0.3, "#ffffff");
      g.addColorStop(0.7, "#d0d0d0");
      g.addColorStop(1, "#a0a0a0");
      ctx.beginPath();
      ctx.moveTo(eyeX - 4, -1.2);
      ctx.bezierCurveTo(eyeX, -1.8, 20, -1.4, L - 6, -0.4);
      ctx.lineTo(L, 0);
      ctx.lineTo(L - 6, 0.4);
      ctx.bezierCurveTo(20, 1.4, eyeX, 1.8, eyeX - 4, 1.2);
      ctx.closePath();
      ctx.fillStyle = g;
      ctx.fill();
      ctx.restore();
      ctx.beginPath();
      ctx.moveTo(eyeX - 2, -0.6);
      ctx.bezierCurveTo(10, -1.1, 35, -0.9, L - 4, -0.2);
      ctx.strokeStyle = "rgba(255,255,255,0.55)";
      ctx.lineWidth = 0.5;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(L - 1, 0, 0.7, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(eyeX, 0, 5, 1.4, 0, 0, Math.PI * 2);
      ctx.fillStyle = "#111";
      ctx.fill();
      const eg = ctx.createLinearGradient(eyeX - 5, -2, eyeX + 5, 2);
      eg.addColorStop(0, "rgba(180,180,180,0.8)");
      eg.addColorStop(1, "rgba(100,100,100,0.4)");
      ctx.strokeStyle = eg;
      ctx.lineWidth = 0.7;
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(eyeX, 0, 5.8, 2.1, 0, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(180,150,80,0.35)";
      ctx.lineWidth = 0.6;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(eyeX, 0);
      ctx.bezierCurveTo(eyeX - 1, -10, eyeX + 1, -20, eyeX, -30);
      ctx.strokeStyle = "rgba(0,0,0,0.45)";
      ctx.lineWidth = 1;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(eyeX + 0.4, 0);
      ctx.bezierCurveTo(eyeX + 0.3, -10, eyeX + 0.8, -20, eyeX + 0.4, -30);
      ctx.strokeStyle = "rgba(200,200,200,0.3)";
      ctx.lineWidth = 0.4;
      ctx.stroke();
      ctx.restore();
    }

    function buildGroups() {
      const groups = [];
      const MF = 'italic bold 108px "Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif';
      const mc = document.createElement("canvas");
      mc.width = W;
      mc.height = H;
      const mx = mc.getContext("2d");
      mx.font = MF;
      const text = "Deen & Zine";
      const mw = mx.measureText(text).width;
      const mStartX = Math.round((W - mw) / 2);
      let cx = mStartX;

      for (const ch of text) {
        const cw = mx.measureText(ch).width;
        if (ch === " ") {
          cx += cw;
          continue;
        }
        const pts = rasterFill(ch, MF, cx, 160, 2);
        if (ch === "i") {
          splitSegments(pts, 15).forEach((seg) => {
            if (seg.length > 1) groups.push({ pts: seg, thin: false });
          });
        } else if (pts.length) {
          groups.push({ pts, thin: false });
        }
        cx += Math.round(cw);
      }

      const underline = [];
      const x1 = mStartX;
      const x2 = mStartX + Math.round(mw);
      for (let x = x1; x <= x2; x += 2) {
        underline.push({ x, y: 182 });
        underline.push({ x, y: 184 });
      }
      groups.push({ pts: underline, thin: false });

      const SF = "bold 19px Arial, sans-serif";
      const sc = document.createElement("canvas");
      sc.width = W;
      sc.height = H;
      const sx = sc.getContext("2d");
      sx.font = SF;
      const word = "ESSENTIALS";
      const sp = 10;
      const wArr = word.split("").map((c) => sx.measureText(c).width);
      const total = wArr.reduce((sum, w) => sum + w, 0) + (word.length - 1) * sp;
      let scx = Math.round((W - total) / 2);
      for (let i = 0; i < word.length; i += 1) {
        const pts = traceContour(word[i], SF, scx, 256);
        if (pts.length) groups.push({ pts, thin: true });
        scx += Math.round(wArr[i]) + sp;
      }
      return groups;
    }

    const start = async () => {
      if (document.fonts?.ready) await document.fonts.ready;
      if (cancelled) return;

      const groups = buildGroups();
      let gi = 0;
      let pi = 0;
      const doneGroups = [];
      let partialPts = [];
      const trail = [];
      const SPF = 72;
      let finalFrames = 0;

      function redrawAll() {
        ctx.clearRect(0, 0, W, H);
        drawFabric();
        doneGroups.forEach((g) => drawGroup(g.pts, undefined, g.thin));
        if (gi < groups.length) drawGroup(partialPts, undefined, groups[gi].thin);
      }

      function frame() {
        if (cancelled) return;

        for (let s = 0; s < SPF; s += 1) {
          if (gi < groups.length) {
            if (pi < groups[gi].pts.length) {
              partialPts.push(groups[gi].pts[pi]);
              pi += 1;
            } else {
              doneGroups.push({ pts: [...partialPts], thin: groups[gi].thin });
              partialPts = [];
              gi += 1;
              pi = 0;
            }
          }
        }

        redrawAll();

        if (gi < groups.length) {
          const pts = groups[gi].pts;
          const p = pts[Math.min(pi, pts.length - 1)];
          const t = Date.now() * 0.01;
          const bob = Math.sin(t * 4) * 1;
          const poke = Math.abs(Math.sin(t * 4)) * 2.5;
          const tipX = p.x;
          const tipY = p.y + bob + poke;
          trail.push({ x: tipX, y: tipY });
          if (trail.length > 22) trail.shift();
          if (trail.length > 2) {
            ctx.beginPath();
            ctx.moveTo(trail[0].x, trail[0].y);
            for (let k = 1; k < trail.length; k += 1) ctx.lineTo(trail[k].x, trail[k].y);
            ctx.strokeStyle = "rgba(0,0,0,0.07)";
            ctx.lineWidth = 0.7;
            ctx.lineCap = "round";
            ctx.stroke();
          }
          drawNeedle(tipX, tipY);
          raf = requestAnimationFrame(frame);
        } else {
          finalFrames += 1;
          if (!finished && finalFrames > 24) {
            finished = true;
            onDone?.();
          }
          if (finalFrames < 70) raf = requestAnimationFrame(frame);
        }
      }

      frame();
    };

    start();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [onDone]);

  return <canvas ref={canvasRef} className="embroidery-canvas" aria-label="Animation du logo brodé Deen & Zine" />;
};

const Slider = ({ slides = [] }) => {
  const [current, setCurrent] = useState(0);
  const [introDone, setIntroDone] = useState(false);
  const [introHidden, setIntroHidden] = useState(false);

  const handleIntroDone = useCallback(() => {
    setIntroDone(true);
  }, []);

  useEffect(() => {
    if (!slides.length || !introDone) return undefined;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 7200);
    return () => clearInterval(interval);
  }, [slides, introDone]);

  useEffect(() => {
    if (!introDone) return undefined;
    const timer = window.setTimeout(() => setIntroHidden(true), 900);
    return () => window.clearTimeout(timer);
  }, [introDone]);

  if (!slides.length) return null;

  const slide = slides[current];

  return (
    <section className={`thread-hero ${introDone ? "is-ready" : ""}`} aria-label="Mise en avant de la collection">
      <div className="thread-hero-media" aria-hidden="true">
        {slides.map((item, index) => (
          <img
            key={index}
            src={item.image}
            alt=""
            className={`thread-hero-image ${index === current ? "is-active" : ""}`}
          />
        ))}
      </div>

      <div className="thread-hero-shade" />

      <div className="container thread-hero-inner">
        <div className="thread-hero-content">
          {slide.eyebrow && <span className="hero-eyebrow dark hero-reveal-1">{slide.eyebrow}</span>}

          <h1 className="hero-title dark hero-reveal-2">
            {slide.title.split("\n").map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < slide.title.split("\n").length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>

          {slide.subtitle && <p className="hero-subtitle dark hero-reveal-3">{slide.subtitle}</p>}

          <div className="hero-actions hero-reveal-4">
            {slide.buttonLink && (
              <a href={slide.buttonLink} className="button">
                {slide.buttonText}
              </a>
            )}

            {slide.secondaryLink && (
              <a href={slide.secondaryLink} className="button button-secondary">
                {slide.secondaryText}
              </a>
            )}
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <div className="hero-dots thread-hero-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`hero-dot ${index === current ? "is-active" : ""}`}
              onClick={() => setCurrent(index)}
              aria-label={`Aller à la slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      <div className="thread-scroll-label" aria-hidden="true">
        <span />
        Défiler
      </div>

      {!introHidden && (
        <div className={`hero-embroidery-intro ${introDone ? "is-complete" : ""}`} aria-hidden={introHidden}>
          <div className="hero-embroidery-card">
            <span className="hero-embroidery-kicker">Maison artisanale</span>
            <EmbroideryCanvas onDone={handleIntroDone} />
            <p>Le fil brode l’identité, puis révèle les pièces.</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Slider;
